import './videocall.scss';
import {
  Avatar,
  Box,
  CircularProgress,
  Fab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  Button,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {useContext, useEffect, useRef, useState} from 'react';
import {
  OfficeContextType,
  VideocallState,
  VideocallStateParticipantInfo,
} from '../../contexts/office/types';
import {OfficeContext} from '../../contexts/office/context';
import {AuthContextType} from '../../contexts/auth/types';
import {AuthContext} from '../../contexts/auth/context';
import Draggable from 'react-draggable';
import CallEndIcon from '@mui/icons-material/CallEnd';
import PictureInPictureAltIcon from '@mui/icons-material/PictureInPictureAlt';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import {ProductLogo} from '../product-logo/product-logo';
import MicIcon from '@mui/icons-material/Mic';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import moment from 'moment';
import * as VoxImplant from 'voximplant-websdk';
import {Logger} from '../../utils/managers/logger';
import PersonIcon from '@mui/icons-material/Person';
import {translations} from '../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {Call} from 'voximplant-websdk/Call/Call';
import eventEmitter from '../../events/event-emitter';
import {CREATE_VIDEOCALL} from '../../events/event-types';
import PresentToAllIcon from '@mui/icons-material/PresentToAll';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import MicOffIcon from '@mui/icons-material/MicOff';
import {CallContext} from '../../enums/call-context';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import {AudioOutputInfo, AudioSourceInfo, VideoSourceInfo} from 'voximplant-websdk/Structures';
import {useDetectTouchDevice} from '../../hooks/touch-device';
import {
  attachCallListeners,
  detachCallListeners as detachVoxCallListeners,
  VideocallCallEventHandlersMap,
} from './videocall-call-events';
import {
  aggressiveReleaseLocalVideo as aggressiveReleaseLocalVideoInternal,
  LocalRendererLike,
  releaseLocalVideo as releaseLocalVideoInternal,
  syncLocalMediaRenderer as syncLocalMediaRendererInternal,
} from './videocall-media';
import {
  clearRemoteVideoHolder as clearRemoteVideoHolderInternal,
  EndpointEvent,
  EndpointMediaEvent,
  handleEndpointAdded,
  handleEndpointRemoved,
  handleRemoteMediaAdded,
  handleRemoteMediaRemoved,
} from './videocall-endpoints';

// use Audio constructor to create HTMLAudioElement
const incomingCallRinging_audio = new Audio('/audio/incoming-call.mp3');
const establishingCall_audio = new Audio('/audio/establishing-call.wav');
const calling_audio = new Audio('/audio/calling.wav');
const callNotMade_audio = new Audio('/audio/call-not-made.wav');
const callDisconnected_audio = new Audio('/audio/call-disconnected.wav');
const spacing = 32;

enum LocalVideoState {
  Undefined = 0,
  Off = 1,
  Starting = 2,
  On = 3,
}

export const Videocall = () => {
  const {t} = useTranslation();
  const [lg] = useState(() => new Logger('Videocall'));
  const authContext: AuthContextType = useContext(AuthContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);
  const remoteVideoHolderRef = useRef<HTMLDivElement | null>(null);
  const localMediaRendererRef = useRef<LocalRendererLike | null>(null);
  const lastLocalMediaRendererRef = useRef<LocalRendererLike | null>(null);
  const lastEndedCallRef = useRef<Call | undefined>(undefined);
  const cleanupTimeoutsRef = useRef<number[]>([]);
  const timerIntervalRef = useRef<number | undefined>(undefined);
  const endpointsMediaRef = useRef<Record<string, string[]>>({});
  const videocallStateRef = useRef<VideocallState>(officeContext.state.videocall);
  const incomingCallStateRef = useRef(officeContext.state.incomingCall);
  const incomingCallRecoveryTimeoutRef = useRef<number | undefined>(undefined);
  const pendingIncomingPushCallIdRef = useRef<string | undefined>(undefined);
  const ignoredIncomingCallIdsWhileBusyRef = useRef<Set<string>>(new Set());
  const callerInfoRef = useRef<VideocallStateParticipantInfo | undefined>(undefined);
  const isTouch = useDetectTouchDevice();

  // Local UI state
  const [incomingCallDimensions] = useState<[number, number]>([300, 160]);
  const [pictureInPictureDimensions] = useState<[number, number]>([300, 200]);
  const [localVideoDimensions] = useState<[number, number]>([234, 132]);
  const [areaWidth, setAreaWidth] = useState<number>(window.innerWidth);
  const [areaHeight, setAreaHeight] = useState<number>(window.innerHeight);
  const [currentTime, setCurrentTime] = useState<string>('');
  const [callTimer, setCallTimer] = useState<string>('');
  const [localVideoState, setLocalVideoState] = useState<LocalVideoState>(
    LocalVideoState.Undefined
  );
  const [micButtonDisabled, setMicButtonDisabled] = useState<boolean>(true);
  const [sharingScreen, setSharingScreen] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [callFailedOpen, setCallFailedOpen] = useState<boolean>(false);
  const [waitingForOutgoingAcceptance, setWaitingForOutgoingAcceptance] = useState<boolean>(false);
  const [callFailedErrorCode, setCallFailedErrorCode] = useState<number>(0);
  const [muted, setMuted] = useState<boolean>(false);
  const [microphones, setMicrophones] = useState<Array<AudioSourceInfo>>([]);
  const [cameras, setCameras] = useState<Array<VideoSourceInfo>>([]);
  const [speakers, setSpeakers] = useState<Array<AudioOutputInfo>>([]);
  const [dismissedRecoveryActionModal, setDismissedRecoveryActionModal] = useState<boolean>(false);
  const [selectedMicrophone, setSelectedMicrophone] = useState<string | number | null>('default');
  const [selectedCamera, setSelectedCamera] = useState<string | number | null>('default');
  const [selectedSpeaker, setSelectedSpeaker] = useState<string | number | null>('default');
  const callEventHandlersRef = useRef<VideocallCallEventHandlersMap>(new WeakMap());

  function patchVideocallState(patch: Partial<VideocallState>) {
    officeContext.updateVideocallState({...videocallStateRef.current, ...patch});
  }

  function resetEndpointsMedia() {
    endpointsMediaRef.current = {};
  }

  function stopCallTimer(resetTimerLabel: boolean = true) {
    if (timerIntervalRef.current) {
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = undefined;
    }

    if (resetTimerLabel) {
      setCallTimer('');
    }
  }

  // Simple stringify helper for SDK event logging
  function simpleStringify(object: unknown) {
    if (!object || typeof object !== 'object') {
      return JSON.stringify({});
    }

    const stringRecord = object as Record<string, unknown>;
    const simpleObject = Object.keys(stringRecord)
      .filter((item) => typeof stringRecord[item] === 'string')
      .reduce<Record<string, string>>((acc, item) => {
        acc[item] = stringRecord[item] as string;
        return acc;
      }, {});
    return JSON.stringify(simpleObject);
  }

  function clearIncomingCallRecoveryTimeout() {
    if (incomingCallRecoveryTimeoutRef.current) {
      window.clearTimeout(incomingCallRecoveryTimeoutRef.current);
      incomingCallRecoveryTimeoutRef.current = undefined;
    }

    pendingIncomingPushCallIdRef.current = undefined;
  }

  // function getVoximplantSilentLog(sdk: any) {
  //   try {
  //     return sdk.getSilentLog();
  //   } catch (error) {
  //     lg.error('Could not read Voximplant silent log.', error);
  //     return [];
  //   }
  // }

  // Start timer
  function startTimer() {
    stopCallTimer(false);

    let time = 0;
    const second = 1;
    timerIntervalRef.current = window.setInterval(() => {
      time = time + second;
      const hours = Math.floor(time / 3600);
      const minutes = Math.floor((time % 3600) / 60);
      const seconds = time % 60;
      const currentTime = `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}:${
        seconds < 10 ? `0${seconds}` : seconds
      }`;

      // Update UI
      setCallTimer(currentTime);
    }, 1000);
  }

  function syncLocalMediaRenderer() {
    return syncLocalMediaRendererInternal({
      localMediaRendererRef,
      lastLocalMediaRendererRef,
    });
  }

  function clearScheduledLocalVideoCleanup() {
    cleanupTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    cleanupTimeoutsRef.current = [];
  }

  // Voximplant Web SDK can keep its internal dedicated mirror stream alive after
  // the remote party hangs up. When that happens, hideLocalVideo() is not enough
  // and the browser camera indicator stays on. This cleanup intentionally reaches
  // into SDK internals as a last-resort workaround for that case.
  function aggressiveReleaseLocalVideo(call?: Call) {
    aggressiveReleaseLocalVideoInternal({
      call,
      logger: lg,
      localMediaRendererRef,
      lastLocalMediaRendererRef,
      setLocalVideoState,
    });
  }

  function scheduleAggressiveLocalVideoCleanup(call?: Call) {
    clearScheduledLocalVideoCleanup();
    [0, 250, 1000, 2500].forEach((delay) => {
      const timeoutId = window.setTimeout(() => aggressiveReleaseLocalVideo(call), delay);
      cleanupTimeoutsRef.current.push(timeoutId);
    });
  }

  async function releaseLocalVideo(call?: Call, notifyCall: boolean = true) {
    await releaseLocalVideoInternal({
      call,
      notifyCall,
      getCurrentCall: () => videocallStateRef.current.currentCall,
      logger: lg,
      localMediaRendererRef,
      lastLocalMediaRendererRef,
      setLocalVideoState,
    });
  }

  // Show or hide the local video preview
  async function showLocalVideo(enable: boolean) {
    try {
      // Get StreamManager
      const streamManager = VoxImplant.Hardware.StreamManager.get();

      // If wants to enable but local video is not enabled
      if (
        enable &&
        (localVideoState === LocalVideoState.Undefined || localVideoState === LocalVideoState.Off)
      ) {
        clearScheduledLocalVideoCleanup();

        // Update UI
        setLocalVideoState(LocalVideoState.Starting);

        // Show it and store the MediaRenderer returned by the SDK
        const mediaRenderer = await streamManager.showLocalVideo(true);
        localMediaRendererRef.current = mediaRenderer;
        lastLocalMediaRendererRef.current = mediaRenderer;

        // Update UI
        setLocalVideoState(LocalVideoState.On);
      } else if (!enable) {
        await releaseLocalVideo();
      }
    } catch (e) {
      // Log
      lg.debug('ERROR Local video already displayed');
    }
  }

  // Toggle the local video preview
  async function toggleShowLocalVideo() {
    try {
      // If local video is not enabled
      if (
        localVideoState === LocalVideoState.Undefined ||
        localVideoState === LocalVideoState.Off
      ) {
        clearScheduledLocalVideoCleanup();

        // Update UI
        setLocalVideoState(LocalVideoState.Starting);

        // Acquire camera first so the track exists before sendVideo is called
        const mediaRenderer = await VoxImplant.Hardware.StreamManager.get().showLocalVideo(true);
        localMediaRendererRef.current = mediaRenderer;
        lastLocalMediaRendererRef.current = mediaRenderer;

        // Now send the acquired track to the remote party
        await sendLocalVideo(true);

        // Update UI
        setLocalVideoState(LocalVideoState.On);
      } else {
        await releaseLocalVideo();
      }
    } catch (e) {
      // Log
      lg.debug('ERROR Local video already displayed');
    }
  }

  // Send the Local Video
  const sendLocalVideo = async (send: boolean) => {
    // Eval Current Call
    if (videocallStateRef.current.currentCall) {
      try {
        await videocallStateRef.current.currentCall.sendVideo(send);
        lg.debug(`Resolved sendVideo(${send})`);
      } catch (e) {
        lg.debug(`ERROR Reject sendVideo(${send})`, e);
        throw e;
      }
    }
  };

  // End Call
  async function endCall() {
    setWaitingForOutgoingAcceptance(false);

    // If call is available
    if (videocallStateRef.current.currentCall) {
      // hangup
      try {
        void releaseLocalVideo(videocallStateRef.current.currentCall, false);
        patchVideocallState({
          open: false,
          callIncoming: false,
          callIncomingAction: undefined,
          callIncomingPendingRecovery: false,
        });
        setMicButtonDisabled(true);
        closeSettings();
        videocallStateRef.current.currentCall.hangup();
      } catch (e) {
        lg.debug('Could not hangup call', e);
      }
    } else {
      // Call already ended - close the screen
      patchVideocallState({
        open: false,
        callTimer: undefined,
      });
    }
  }

  // Clear Data
  function cleanData() {
    stopCallTimer();
    clearRemoteVideoHolder();
    resetEndpointsMedia();
    setSharingScreen(false);
    setMuted(false);
    setWaitingForOutgoingAcceptance(false);

    // Reset Call timer
    patchVideocallState({
      callTimer: '00:00:00',
    });
  }

  // Handle remote media stream added
  function onRemoteMediaAdded(data: EndpointMediaEvent) {
    handleRemoteMediaAdded({
      data,
      remoteVideoHolderRef,
      endpointsMediaRef,
      participantName: callerInfoRef.current?.name || data.endpoint.displayName,
      isConferenceCall: Boolean(videocallStateRef.current.currentCall?.settings.isConference),
    });
  }

  // Handle the remote media removed
  function onRemoteMediaRemoved(data: EndpointMediaEvent) {
    handleRemoteMediaRemoved({
      data,
      remoteVideoHolderRef,
      endpointsMediaRef,
      participantName: callerInfoRef.current?.name || data.endpoint.displayName,
      isConferenceCall: Boolean(videocallStateRef.current.currentCall?.settings.isConference),
    });
  }

  // Handles endpoint added
  function onEndpointAdded(data: EndpointEvent) {
    handleEndpointAdded({
      data,
      remoteVideoHolderRef,
      endpointsMediaRef,
      onRemoteMediaAdded,
      onRemoteMediaRemoved,
      onEndpointRemoved,
    });
  }

  // Handle an endpoint removed
  function onEndpointRemoved(data: EndpointEvent) {
    handleEndpointRemoved({
      data,
      remoteVideoHolderRef,
      endpointsMediaRef,
      onRemoteMediaAdded,
      onRemoteMediaRemoved,
      onEndpointRemoved,
    });
  }

  function callStateConnected(video: boolean) {
    void video;
    setWaitingForOutgoingAcceptance(false);
    setMicButtonDisabled(false);
  }

  function clearRemoteVideoHolder() {
    clearRemoteVideoHolderInternal(remoteVideoHolderRef);
  }

  function detachCallListeners(call: Call) {
    detachVoxCallListeners({
      call,
      handlersMap: callEventHandlersRef.current,
      onEndpointAdded,
    });
  }

  // Return UI elements to the initial state before call
  function callStateDisconnected(currentCall: Call) {
    // Hide incoming call notification
    closeBackgroundIncomingCallNotification(currentCall);
    setWaitingForOutgoingAcceptance(false);

    // Update UI
    patchVideocallState({
      callIncoming: false,
      callIncomingAction: undefined,
      callIncomingPendingRecovery: false,
      open: false,
      callTimer: undefined,
      currentCall: undefined,
    });

    // Stop audios
    incomingCallRinging_audio.pause();
    establishingCall_audio.pause();
    calling_audio.pause();
    callNotMade_audio.pause();

    incomingCallRinging_audio.currentTime =
      establishingCall_audio.currentTime =
      calling_audio.currentTime =
        0;

    stopCallTimer();

    // Disable Mic button
    setMicButtonDisabled(true);

    // Hide Settings
    closeSettings();

    // Turn off camera
    lastEndedCallRef.current = currentCall;
    scheduleAggressiveLocalVideoCleanup(currentCall);
    void releaseLocalVideo(currentCall, false);

    // Play sound
    callDisconnected_audio.play().catch(() => {});
    setTimeout(() => callDisconnected_audio.pause(), 300);

    // Stop screen share
    setSharingScreen(false);
    setMuted(false);

    // Make sure remote video is empty
    clearRemoteVideoHolder();
    resetEndpointsMedia();

    // remove listeners
    detachCallListeners(currentCall);
  }

  // Set up call
  function setUpCall(callSetup: {
    currentCall: Call;
    isIncoming: boolean; // Is an incoming call
    number: string; // Target phone number
    viewer: boolean; // Join conference as a viewer
    video: boolean; // Is videocall? (Has video incoming or outgoing)
  }) {
    attachCallListeners(callSetup, {
      handlersMap: callEventHandlersRef.current,
      onEndpointAdded,
      logger: lg,
      simpleStringify,
      startTimer,
      stopCallTimer,
      callStateConnected,
      callStateDisconnected,
      openCallFailed,
      establishingCallAudio: establishingCall_audio,
      callingAudio: calling_audio,
      callNotMadeAudio: callNotMade_audio,
    });
  }

  // Create a call or conference
  async function createCall(
    toNumber: string,
    context?: CallContext,
    contextId?: string,
    contextEntityId?: string,
    calleeName?: string
  ) {
    callerInfoRef.current = calleeName
      ? {id: toNumber, name: calleeName, profilePicture: ''}
      : undefined;
    lastEndedCallRef.current = undefined;
    clearScheduledLocalVideoCleanup();

    // Start local preview immediately so call setup behaves as before
    await showLocalVideo(true);

    // Clear Data
    cleanData();
    setWaitingForOutgoingAcceptance(true);

    // Call media configuration
    const sendVideo = true;
    const receiveVideo = true;
    const H264first = true;
    const simulcast = true;

    // Set call settings
    const extraHeaders: Record<string, string> = {};
    if (context) {
      extraHeaders['X-Context'] = context;
    }
    if (contextId) {
      extraHeaders['X-ContextId'] = contextId;
    }
    if (contextEntityId) {
      extraHeaders['X-ContextEntityId'] = contextEntityId;
    }

    const callSettings: {
      number: string;
      extraHeaders?: Record<string, string>;
      video?: {sendVideo: boolean; receiveVideo: boolean};
      H264first?: boolean;
      simulcast?: boolean;
    } = {
      number: toNumber,
      ...(Object.keys(extraHeaders).length > 0 ? {extraHeaders} : {}),
    };

    // Eval flags
    if (receiveVideo && !sendVideo) {
      // Append Video properties
      callSettings.video = {sendVideo, receiveVideo};

      // Set H264 setting
      callSettings.H264first = H264first;
    }

    // Eval flags
    if (sendVideo) {
      // Append Video properties
      callSettings.video = {sendVideo, receiveVideo};
    }

    // Get SDK
    const sdk = VoxImplant.getInstance();

    const isConferenceCall = false;
    let currentCall: Call | undefined = undefined;

    // Log
    lg.debug('Create Call', callSettings);

    // Eval
    if (isConferenceCall) {
      // Set Simulcast setting
      callSettings.simulcast = simulcast;

      // Create Conference
      currentCall = sdk.callConference(callSettings);

      // Update UI
      patchVideocallState({
        currentCall,
        callIncoming: false,
        callIncomingAction: undefined,
        callIncomingPendingRecovery: false,
        open: true,
      });
      setMicButtonDisabled(false);
    } else {
      // Create Call
      currentCall = sdk.call(callSettings);

      // Update UI
      patchVideocallState({
        currentCall,
        callIncoming: false,
        callIncomingAction: undefined,
        callIncomingPendingRecovery: false,
        open: true,
      });
      setMicButtonDisabled(false);
    }

    // Set up Call
    setUpCall({
      currentCall,
      isIncoming: false,
      number: callSettings.number,
      video: sendVideo || receiveVideo,
      viewer: false,
    });
  }

  // Handle Incoming Call
  function handleIncomingCall(call: Call, action?: string) {
    lg.debug('Voximplant handleIncomingCall called.', action);

    authContext.clearPendingForegroundVoximplantPushRecovery(
      'Voximplant IncomingCall event handled',
      {
        clearPlaceholder: true,
      }
    );

    // Clear Data
    cleanData();

    // Parse Caller info
    let callerInfo: VideocallStateParticipantInfo | undefined = undefined;
    if (call.settings.extraHeaders) {
      callerInfo = {
        id: call.settings.extraHeaders['X-Id'],
        name: call.settings.extraHeaders['X-Name'],
        profilePicture: call.settings.extraHeaders['X-ProfilePicture'],
      };
    }
    callerInfoRef.current = callerInfo;

    // Update UI
    patchVideocallState({
      currentCall: call,
      callIncoming: true,
      callIncomingAction: action,
      callIncomingPendingRecovery: false,
      callerInfo,
    });

    // Start Ringing
    incomingCallRinging_audio.loop = true;
    incomingCallRinging_audio.play().catch(() => {});

    // Set up Call
    setUpCall({
      currentCall: call,
      isIncoming: true,
      number: call.number(),
      video: true,
      viewer: false,
    });
  }

  // Mute/unmute audio
  function muteAudio(mute: boolean) {
    const currentCall = videocallStateRef.current.currentCall;

    // Eval
    if (currentCall) {
      if (mute) {
        // Mute
        currentCall.muteMicrophone();
      } else {
        // Unmute
        currentCall.unmuteMicrophone();
      }

      // Update UI
      setMuted(mute);
    }
  }

  // Open settings
  async function openSettings() {
    // Update Cameras
    const cams = (await VoxImplant.Hardware.CameraManager.get().getInputDevices()) || [];
    if (!cams.map((cam) => cam.id).includes('default')) {
      cams.unshift({id: 'default', name: t(translations.videocall.default)} as VideoSourceInfo);
    }
    setCameras(cams);
    // Is current Camera still on the list?
    if (selectedCamera && !cams.map((cam) => cam.id).includes(selectedCamera)) {
      // No! Select the first one available
      if (cams.length > 0) {
        changeCamera(cams[0].id);
      } else {
        changeCamera(null);
      }
    }

    // Update Mics List
    const allMics: Array<AudioSourceInfo> = [];
    const cameraGroups = new Set(cams.map((cam) => cam.group));
    const mics = (await VoxImplant.Hardware.AudioDeviceManager.get().getInputDevices()) || [];
    if (!mics.map((mic) => mic.id).includes('default')) {
      mics.unshift({id: 'default', name: t(translations.videocall.default)} as AudioSourceInfo);
    }
    mics
      .filter((mic) => !cameraGroups.has(mic.group))
      .forEach((mic) => {
        allMics.push(mic);
      });
    setMicrophones(allMics);
    // Is current Microphone still on the list?
    if (selectedMicrophone && !allMics.map((mic) => mic.id).includes(selectedMicrophone)) {
      // No! Select the first one available
      if (allMics.length > 0) {
        changeMicrophone(allMics[0].id);
      } else {
        changeMicrophone(null);
      }
    }

    // Speakers
    const speakers = (await VoxImplant.Hardware.AudioDeviceManager.get().getOutputDevices()) || [];
    if (!speakers.map((speaker) => speaker.id).includes('default')) {
      speakers.unshift({id: 'default', name: t(translations.videocall.default)} as AudioOutputInfo);
    }
    setSpeakers(speakers);
    // Is current Speaker still on the list?
    if (selectedSpeaker && !speakers.map((speaker) => speaker.id).includes(selectedSpeaker)) {
      // No! Select the first one available
      if (speakers.length > 0) {
        changeSpeaker(speakers[0].id);
      } else {
        changeSpeaker(null);
      }
    }

    // Open menu
    setSettingsOpen(true);
  }

  // Close settings
  function closeSettings() {
    setSettingsOpen(false);
  }

  // Set up a device according to audio/video settings
  function changeMicrophone(deviceId: string | number | null) {
    // Check
    if (deviceId) {
      const inputId = String(deviceId);

      // Map parms
      const audioParams: {inputId: string; outputId?: string} = {inputId};

      // Get output id
      const outputId =
        VoxImplant.Hardware.AudioDeviceManager.get().getDefaultAudioSettings().outputId;

      // Check output id
      if (outputId) {
        // Override params
        audioParams.outputId = outputId;
      }

      // If a call is running
      if (videocallStateRef.current.currentCall) {
        // Change right now
        VoxImplant.Hardware.AudioDeviceManager.get().setCallAudioSettings(
          videocallStateRef.current.currentCall,
          audioParams
        );
      }

      // Change defaults
      VoxImplant.Hardware.AudioDeviceManager.get().setDefaultAudioSettings(audioParams);
    }

    // Update UI
    setSelectedMicrophone(deviceId);
  }

  // Set up a device according to audio/video settings
  function changeSpeaker(deviceId: string | number | null) {
    // Check
    if (deviceId) {
      const outputId = String(deviceId);

      // Get Input
      const inputId =
        VoxImplant.Hardware.AudioDeviceManager.get().getDefaultAudioSettings().inputId;

      // Map parms
      const audioParams: {outputId: string; inputId?: string} = {outputId};

      // Check
      if (inputId) {
        // Override params
        audioParams.inputId = inputId;
      }

      // If a call is running
      if (videocallStateRef.current.currentCall) {
        // Change right now
        VoxImplant.Hardware.AudioDeviceManager.get().setCallAudioSettings(
          videocallStateRef.current.currentCall,
          audioParams
        );
      }

      // Change defaults
      VoxImplant.Hardware.AudioDeviceManager.get().setDefaultAudioSettings(audioParams);
    }

    // Update UI
    setSelectedSpeaker(deviceId);
  }

  // Set up a device according to audio/video settings
  function changeCamera(deviceId: string | number | null) {
    // Check
    if (deviceId) {
      const cameraId = String(deviceId);

      // If a call is running
      if (videocallStateRef.current.currentCall) {
        VoxImplant.Hardware.CameraManager.get().setCallVideoSettings(
          videocallStateRef.current.currentCall,
          {cameraId}
        );
      }

      // Change defaults
      VoxImplant.Hardware.CameraManager.get().setDefaultVideoSettings({cameraId});
    }

    // Update UI
    setSelectedCamera(deviceId);
  }

  // Answer Call
  function answerCall() {
    setWaitingForOutgoingAcceptance(false);
    const currentCall = videocallStateRef.current.currentCall;

    // Accept call
    if (currentCall) {
      lastEndedCallRef.current = undefined;
      clearScheduledLocalVideoCleanup();

      // Start local preview immediately so answer flow behaves as before
      void showLocalVideo(true);

      // Do it
      currentCall.answer(
        '',
        {},
        {
          sendVideo: true,
          receiveVideo: true,
        }
      );

      // Update UI
      patchVideocallState({
        open: true,
        callIncoming: false,
        callIncomingAction: undefined,
        callIncomingPendingRecovery: false,
      });
      setMicButtonDisabled(false);

      // Stop Ringing
      incomingCallRinging_audio.pause();
      incomingCallRinging_audio.currentTime = 0;

      // Hide incoming call notification
      closeBackgroundIncomingCallNotification(currentCall);
    }
  }

  // Decline Call
  function declineCall() {
    const currentCall = videocallStateRef.current.currentCall;

    // Decline call
    if (currentCall) {
      currentCall.decline();
    }
  }

  // Close Background Incoming Call Notification
  async function closeBackgroundIncomingCallNotification(call?: Call) {
    // Get call Id
    let callId: string = '';
    if (call && call.settings.extraHeaders) {
      callId = call.settings.extraHeaders['VI-Call-ID'];
    }

    // Post Message
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller?.postMessage({
        type: 'CLOSE_INCOMING_CALL_NOTIFICATION',
        callId,
      });
    } else {
      lg.warn('Could not close background incoming call notification because controller is null');
    }
  }

  // Close call failed dialog
  function closeCallFailed() {
    // Hide dialog
    setCallFailedOpen(false);

    // Reset error
    setTimeout(() => {
      // Wait a bit to do not disturb animation
      setCallFailedErrorCode(0);
    }, 100);
  }

  // Open call failed dialog
  function openCallFailed(code: number, reason: string) {
    // Set error
    // https://voximplant.com/docs/references/websdk/voximplant/eventhandlers/failed#code
    // CODE	DESCRIPTION
    // 486	Destination number is busy
    // 487	Request terminated
    // 603	Call was rejected
    // 404	Invalid number
    // 480	Destination number is unavailable
    // 402	Insufficient funds
    setCallFailedErrorCode(code);

    // Show dialog
    setCallFailedOpen(true);
  }

  function startScreenShare() {
    const currentCall = videocallStateRef.current.currentCall;

    // Check if a call is ongoing
    if (currentCall) {
      const showLocalVideoSharing = true;
      const replaceVideo = true;

      // Share Screen
      currentCall
        .shareScreen(showLocalVideoSharing, replaceVideo)
        .then(() => {
          if (videocallStateRef.current.currentCall) {
            // Log
            lg.debug('Sharing attached');
            // Update UI
            setSharingScreen(true);

            const applyScreenShareQualitySettings = () => {
              currentCall.peerConnection
                .getTransceivers()
                .map((transceiver) => transceiver.sender)
                .filter((sender) => sender.track?.kind === 'video')
                .forEach((sender) => {
                  const sharingTrack = sender.track;
                  if (!sharingTrack) return;

                  // Prefer sharp text/details over motion smoothness for screen sharing.
                  sharingTrack.contentHint = 'detail';

                  // Quality settings for screen sharing.
                  const screenshareSettings = {
                    frameRate: 15,
                    bitrate: 2.5 * 1000 * 1000, // 2.5 Mbps
                    downscale: 1, // No downscaling
                  };

                  sharingTrack
                    .applyConstraints({
                      frameRate: {
                        ideal: screenshareSettings.frameRate,
                        max: screenshareSettings.frameRate,
                      },
                    })
                    .catch((e: unknown) => {
                      lg.debug(
                        'Could not apply screen share track constraints.',
                        e instanceof Error ? e.message : String(e)
                      );
                    });

                  const senderParams = sender.getParameters();
                  senderParams.encodings = senderParams.encodings?.length
                    ? senderParams.encodings
                    : [{}];

                  senderParams.encodings.forEach((encoding: RTCRtpEncodingParameters) => {
                    encoding.maxBitrate = screenshareSettings.bitrate;
                    encoding.maxFramerate = screenshareSettings.frameRate;
                    encoding.scaleResolutionDownBy = screenshareSettings.downscale;
                  });
                  sender.setParameters(senderParams).catch((e: unknown) => {
                    lg.debug(
                      'Could not apply screen share quality settings.',
                      e instanceof Error ? e.message : String(e)
                    );
                  });

                  // Screen sharing can be closed not only with an interface button but also via native browser component.
                  sharingTrack.addEventListener(
                    'ended',
                    () => {
                      // Update UI
                      setSharingScreen(false);
                    },
                    {once: true}
                  );
                });
            };

            applyScreenShareQualitySettings();
            // window.setTimeout(applyScreenShareQualitySettings, 250);
            // window.setTimeout(applyScreenShareQualitySettings, 1000);
          }
        })
        .catch((e: unknown) => {
          // Log
          lg.debug('Screen Share failed.', e instanceof Error ? e.message : String(e));
        });
    }
  }
  // Stop screen sharing
  function stopScreenShare() {
    const currentCall = videocallStateRef.current.currentCall;

    // Eval call ongoing
    if (currentCall) {
      currentCall
        .stopSharingScreen()
        .then(() => {
          // Log
          lg.debug('Sharing detached');
          // Update UI
          setSharingScreen(false);
        })
        .catch((e: unknown) => {
          // Log
          lg.debug(e);
        });
    }
  }

  useEffect(() => {
    videocallStateRef.current = officeContext.state.videocall;
  }, [officeContext.state.videocall]);

  useEffect(() => {
    incomingCallStateRef.current = officeContext.state.incomingCall;
  }, [officeContext.state.incomingCall]);

  // On mount
  useEffect(() => {
    // On window resize
    const onWindowResize = () => {
      // Update Areas
      setAreaWidth(window.innerWidth);
      setAreaHeight(window.innerHeight);
    };
    const streamManager = VoxImplant.Hardware.StreamManager.get();
    const onLocalRendererChanged = (event?: {
      renderer?: {isLocal?: boolean; kind?: string};
      type?: string;
    }) => {
      if (
        !event ||
        (event.renderer?.isLocal && (event.renderer.kind === 'video' || event.type === 'video'))
      ) {
        syncLocalMediaRenderer();
      }
    };

    window.addEventListener('resize', onWindowResize);
    streamManager.on(VoxImplant.Hardware.HardwareEvents.MediaRendererAdded, onLocalRendererChanged);
    streamManager.on(
      VoxImplant.Hardware.HardwareEvents.MediaRendererUpdated,
      onLocalRendererChanged
    );
    streamManager.on(
      VoxImplant.Hardware.HardwareEvents.MediaRendererRemoved,
      onLocalRendererChanged
    );

    // On time tick
    setCurrentTime(moment().format('LT'));
    const onTimeTick = window.setInterval(() => {
      setCurrentTime(moment().format('LT'));
    }, 1000);

    // On unmount
    return () => {
      // Unsubscribe from all
      window.removeEventListener('resize', onWindowResize);
      window.clearInterval(onTimeTick);
      stopCallTimer();
      streamManager.off(
        VoxImplant.Hardware.HardwareEvents.MediaRendererAdded,
        onLocalRendererChanged
      );
      streamManager.off(
        VoxImplant.Hardware.HardwareEvents.MediaRendererUpdated,
        onLocalRendererChanged
      );
      streamManager.off(
        VoxImplant.Hardware.HardwareEvents.MediaRendererRemoved,
        onLocalRendererChanged
      );
      clearScheduledLocalVideoCleanup();
      aggressiveReleaseLocalVideo(
        lastEndedCallRef.current ?? videocallStateRef.current.currentCall
      );
      void releaseLocalVideo(
        lastEndedCallRef.current ?? videocallStateRef.current.currentCall,
        false
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Failsafe hack: after a call disappears from state, retry cleanup a few times
  // because Voximplant may still hold internal mirror/call streams for a moment.
  useEffect(() => {
    if (officeContext.state.videocall.currentCall || !lastEndedCallRef.current) {
      return () => {
        clearScheduledLocalVideoCleanup();
      };
    }

    scheduleAggressiveLocalVideoCleanup(lastEndedCallRef.current);
    return () => {
      clearScheduledLocalVideoCleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeContext.state.videocall.currentCall]);

  // Listen for incoming/outgoing calls
  useEffect(() => {
    const onCreateVideocall = (
      toNumber: string,
      context?: CallContext,
      contextId?: string,
      contextEntityId?: string,
      calleeName?: string
    ) => {
      createCall(toNumber, context, contextId, contextEntityId, calleeName);
    };

    eventEmitter.on(CREATE_VIDEOCALL, onCreateVideocall);

    return () => {
      eventEmitter.off(CREATE_VIDEOCALL, onCreateVideocall);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to SDK incoming call events once and read the latest state via refs.
  useEffect(() => {
    const sdk = VoxImplant.getInstance();
    const onIncomingCall = (e: {call: Call}) => {
      clearIncomingCallRecoveryTimeout();

      const incomingSdkCallId = e.call.settings.extraHeaders?.['VI-Call-ID'] ?? e.call.id();
      if (ignoredIncomingCallIdsWhileBusyRef.current.has(incomingSdkCallId)) {
        lg.debug(
          'Ignoring Voximplant IncomingCall because this call was previously ignored while another call was ongoing.',
          {
            incomingCallId: incomingSdkCallId,
          }
        );
        officeContext.setIncomingCall(undefined);
        authContext.clearPendingForegroundVoximplantPushRecovery(
          'ignoring previously busy-time Voximplant IncomingCall',
          {
            clearPlaceholder: true,
          }
        );
        return;
      }

      if (videocallStateRef.current.currentCall && !videocallStateRef.current.callIncoming) {
        ignoredIncomingCallIdsWhileBusyRef.current.add(incomingSdkCallId);
        lg.debug('Ignoring Voximplant IncomingCall because a call is already ongoing.', {
          currentCallId:
            videocallStateRef.current.currentCall.settings.extraHeaders?.['VI-Call-ID'] ??
            videocallStateRef.current.currentCall.id(),
          incomingCallId: incomingSdkCallId,
        });
        officeContext.setIncomingCall(undefined);
        authContext.clearPendingForegroundVoximplantPushRecovery(
          'ignoring Voximplant IncomingCall because a call is already ongoing',
          {
            clearPlaceholder: true,
          }
        );
        return;
      }

      const incomingCallState = incomingCallStateRef.current;

      if (
        incomingCallState &&
        incomingCallState.notification?.voximplant &&
        (!incomingCallState.appAlreadyOpened || !videocallStateRef.current.callIncoming)
      ) {
        handleIncomingCall(e.call, incomingCallState.action);
        officeContext.setIncomingCall(undefined);
        return;
      }

      handleIncomingCall(e.call);
    };

    sdk.on(VoxImplant.Events.IncomingCall, onIncomingCall);
    lg.debug('Voximplant handleIncomingCall defined.');

    return () => {
      clearIncomingCallRecoveryTimeout();
      sdk.off(VoxImplant.Events.IncomingCall, onIncomingCall);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to incoming call actions / push notifications.
  useEffect(() => {
    const sdk = VoxImplant.getInstance();
    const incomingCallState = officeContext.state.incomingCall;

    if (!incomingCallState) {
      return;
    }

    if (ignoredIncomingCallIdsWhileBusyRef.current.has(incomingCallState.callId)) {
      lg.debug(
        'Ignoring incoming call notification because this call was previously ignored while another call was ongoing.',
        {
          incomingCallId: incomingCallState.callId,
          action: incomingCallState.action,
        }
      );
      clearIncomingCallRecoveryTimeout();
      officeContext.setIncomingCall(undefined);
      authContext.clearPendingForegroundVoximplantPushRecovery(
        'ignoring previously busy-time incoming-call notification',
        {
          clearPlaceholder: true,
        }
      );
      return;
    }

    if (videocallStateRef.current.currentCall && !videocallStateRef.current.callIncoming) {
      ignoredIncomingCallIdsWhileBusyRef.current.add(incomingCallState.callId);
      lg.debug('Ignoring incoming call notification handling because a call is already ongoing.', {
        currentCallId:
          videocallStateRef.current.currentCall.settings.extraHeaders?.['VI-Call-ID'] ??
          videocallStateRef.current.currentCall.id(),
        incomingCallId: incomingCallState.callId,
        action: incomingCallState.action,
      });
      clearIncomingCallRecoveryTimeout();
      officeContext.setIncomingCall(undefined);
      authContext.clearPendingForegroundVoximplantPushRecovery(
        'ignoring incoming-call notification because a call is already ongoing',
        {
          clearPlaceholder: true,
        }
      );
      return;
    }

    if (incomingCallState.appAlreadyOpened && videocallStateRef.current.callIncoming) {
      lg.debug('Incoming Call from open state', incomingCallState.callId);

      if (incomingCallState.action !== '') {
        if (incomingCallState.action === 'accept') {
          answerCall();
        } else if (incomingCallState.action === 'decline') {
          declineCall();
        }

        console.debug('⌛️ Incoming Call Dialog shown. Taking action: ' + incomingCallState.action);
      }

      officeContext.setIncomingCall(undefined);
      return;
    }

    if (incomingCallState.appAlreadyOpened) {
      lg.warn(
        'Incoming call notification click was received while the app tab was already open but no incoming call UI was visible. Falling back to Voximplant push recovery.',
        incomingCallState
      );
    } else {
      lg.debug('Incoming Call from quit state', incomingCallState.callId);
    }

    let pushPayload: {callid?: string};
    try {
      pushPayload = JSON.parse(incomingCallState.notification.voximplant);
    } catch (e) {
      lg.error('Could not parse incoming Voximplant push notification.', e);
      officeContext.setIncomingCall(undefined);
      return;
    }

    const incomingPushDisposition = authContext.deferVoximplantIncomingPushUntilHealthy(
      incomingCallState.notification,
      incomingCallState.action,
      'quit-state incoming call push handling'
    );

    if (incomingPushDisposition !== 'ready') {
      if (incomingPushDisposition === 'duplicate-visible-ui') {
        lg.debug(
          'Skipping incoming Voximplant push recovery because the incoming call UI is already visible.'
        );
      } else {
        lg.debug('Deferring incoming Voximplant push handling until Voximplant is healthy again.');
      }
      clearIncomingCallRecoveryTimeout();
      officeContext.setIncomingCall(undefined);
      return;
    }

    const tryRecoverIncomingCall = (callId: string, attempt: string) => {
      try {
        const recoveredCall = sdk.getCall(callId);

        if (!recoveredCall) {
          return false;
        }

        lg.debug(
          'IncomingCall event did not fire after push handling. Recovering call from Voximplant SDK.',
          {
            callId,
            attempt,
          }
        );
        handleIncomingCall(recoveredCall);
        officeContext.setIncomingCall(undefined);
        clearIncomingCallRecoveryTimeout();
        return true;
      } catch (recoveryError) {
        lg.error('Could not recover incoming Voximplant call after push handling.', {
          callId,
          attempt,
          recoveryError,
        });
        return false;
      }
    };

    const recoverThroughVoximplantReconnect = (failureReason: string) => {
      if (!incomingCallState.appAlreadyOpened) {
        return;
      }

      void authContext.recoverVoximplantIncomingPush(
        incomingCallState.notification,
        failureReason,
        incomingCallState.action
      );
    };

    clearIncomingCallRecoveryTimeout();
    pendingIncomingPushCallIdRef.current = pushPayload.callid;
    sdk.clearSilentLog();

    sdk
      .handlePushNotification(pushPayload)
      .then(() => {
        console.log('Notification handled to Voximplant', incomingCallState.notification);

        incomingCallRecoveryTimeoutRef.current = window.setTimeout(() => {
          const callId = pendingIncomingPushCallIdRef.current;

          if (!callId || videocallStateRef.current.callIncoming) {
            clearIncomingCallRecoveryTimeout();
            return;
          }

          if (tryRecoverIncomingCall(callId, 'parsed Voximplant payload')) {
            return;
          }

          lg.debug(
            'IncomingCall event did not fire after parsed Voximplant push handling. Retrying with full notification payload.',
            {
              callId,
            }
          );

          sdk.clearSilentLog();
          sdk
            .handlePushNotification(incomingCallState.notification)
            .then(() => {
              console.log(
                'Notification handled to Voximplant with full payload',
                incomingCallState.notification
              );

              incomingCallRecoveryTimeoutRef.current = window.setTimeout(() => {
                const retryCallId = pendingIncomingPushCallIdRef.current;

                if (!retryCallId || videocallStateRef.current.callIncoming) {
                  clearIncomingCallRecoveryTimeout();
                  return;
                }

                if (tryRecoverIncomingCall(retryCallId, 'full notification payload')) {
                  return;
                }

                lg.warn(
                  'Voximplant push was handled but no IncomingCall event was fired and no call could be recovered after trying both payload formats.',
                  {
                    callId: retryCallId,
                  }
                );
                // recoverThroughVoximplantReconnect(
                //   'incoming-call notification click push handling completed without IncomingCall or recoverable call'
                // );
                // officeContext.setIncomingCall(undefined);
                // clearIncomingCallRecoveryTimeout();
                // authContext.clearPendingForegroundVoximplantPushRecovery(
                //   'no IncomingCall event after push handling',
                //   {clearPlaceholder: true}
                // );
                // const clientState = sdk.getClientState();
                // const isConnecting = clientState !== ClientState.DISCONNECTED;
                // if (!isConnecting) {
                //   lg.warn('Voximplant client is not connecting. Disconnecting...');
                //   sdk.disconnect();
                // }
                sdk.disconnect();
              }, 5000);
            })
            .catch((retryError) => {
              lg.error(
                'Retrying incoming Voximplant push handling with full notification payload failed.',
                {
                  callId,
                  retryError,
                }
              );
              recoverThroughVoximplantReconnect(
                'retrying incoming-call notification click push handling with the full payload failed'
              );
              officeContext.setIncomingCall(undefined);
              clearIncomingCallRecoveryTimeout();
            });
        }, 1500);
      })
      .catch((e) => {
        console.log(
          'Notification failed to be handled to Voximplant',
          e,
          incomingCallState.notification
        );
        lg.error('Primary incoming Voximplant push handling failed.', e);
        recoverThroughVoximplantReconnect(
          'primary incoming-call notification click push handling failed'
        );
        clearIncomingCallRecoveryTimeout();
        officeContext.setIncomingCall(undefined);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeContext.state.incomingCall]);

  // Act in for Background quit Incoming Calls
  useEffect(() => {
    // Decide
    if (officeContext.state.videocall.callIncomingAction === 'accept') {
      // Answer
      answerCall();
    } else if (officeContext.state.videocall.callIncomingAction === 'decline') {
      // Decline
      declineCall();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeContext.state.videocall.callIncomingAction]);

  const actualIncomingCallPendingRecovery =
    !!officeContext.state.videocall.callIncomingPendingRecovery;
  //const actualIncomingCallPendingRecovery = false; // For testing the recovery flow without needing to trigger real push notifications
  const actualIncomingCallVisible = !!officeContext.state.videocall.callIncoming;
  // const actualIncomingCallVisible = !actualIncomingCallPendingRecovery;
  const actualIncomingCallVisibleWithoutRecovery =
    actualIncomingCallVisible && !actualIncomingCallPendingRecovery;
  const incomingCallPendingRecovery = actualIncomingCallPendingRecovery;
  const incomingCallVisible =
    actualIncomingCallVisibleWithoutRecovery ||
    (actualIncomingCallPendingRecovery && !dismissedRecoveryActionModal);
  const incomingCallCallerName = officeContext.state.videocall.callerInfo?.name ?? '';
  const incomingCallPopupWidth = incomingCallPendingRecovery ? 300 : incomingCallDimensions[0];
  const incomingCallPopupHeight = incomingCallPendingRecovery ? 162 : incomingCallDimensions[1];

  useEffect(() => {
    if (actualIncomingCallPendingRecovery || actualIncomingCallVisibleWithoutRecovery) {
      setDismissedRecoveryActionModal(false);
    }
  }, [actualIncomingCallPendingRecovery, actualIncomingCallVisibleWithoutRecovery]);

  return (
    <>
      <Draggable
        defaultClassName={`videocall-draggable ${officeContext.state.videocall.open ? 'visible' : 'hidden'}`}
        bounds="parent"
        disabled={officeContext.state.videocall.screenMode === 'full-screen' || isTouch}
      >
        <Box
          sx={
            officeContext.state.videocall.screenMode !== 'full-screen'
              ? undefined
              : {
                  left: areaWidth - spacing - pictureInPictureDimensions[0],
                  top: areaHeight - spacing - pictureInPictureDimensions[1],
                  width: pictureInPictureDimensions[0],
                  height: pictureInPictureDimensions[1],
                }
          }
          className={`videocall-area ${officeContext.state.videocall.screenMode}`}
        >
          <Box className="body">
            <Box className="incoming-video">
              <Box id="remote_video_holder" ref={remoteVideoHolderRef}></Box>
              {waitingForOutgoingAcceptance && (
                <Box className="outgoing-call-waiting">
                  <CircularProgress
                    color="control"
                    size={
                      officeContext.state.videocall.screenMode === 'picture-in-picture' ? 40 : 56
                    }
                  />
                </Box>
              )}
            </Box>
            <Draggable
              defaultClassName="videocall-local-video"
              bounds="parent"
              defaultPosition={{
                x: areaWidth - spacing - localVideoDimensions[0],
                y: areaHeight - 80 - spacing - localVideoDimensions[1],
              }}
              disabled={
                officeContext.state.videocall.screenMode === 'picture-in-picture' || isTouch
              }
            >
              <Box
                className="local-video"
                sx={
                  officeContext.state.videocall.screenMode === 'full-screen'
                    ? {
                        left: areaWidth - spacing,
                        top: areaHeight - spacing * 0.5 - 80,
                        width: `${localVideoDimensions[0]}px`,
                        height: `${localVideoDimensions[1]}px`,
                      }
                    : {}
                }
              >
                {localVideoState === LocalVideoState.Starting && (
                  <Box className="loading-camera">
                    <CircularProgress color="control" />
                  </Box>
                )}
                {localVideoState === LocalVideoState.Off && (
                  <Box className="camera-off">
                    <Avatar
                      className="avatar"
                      alt={
                        authContext.state.session ? authContext.state.session.currentUser.name : ''
                      }
                      src={
                        authContext.state.session && authContext.state.session.currentUser.picture
                          ? authContext.state.session.currentUser.picture
                          : ''
                      }
                    >
                      <PersonIcon
                        sx={{
                          fontSize: `48px`,
                        }}
                      />
                    </Avatar>
                  </Box>
                )}
                <Box id="local_video_holder"></Box>
                <Box className="label">{t(translations.videocall.you)}</Box>
              </Box>
            </Draggable>
          </Box>
          <Box className="footer">
            <Box className="left">
              <Box className="product-logo">
                <ProductLogo usePrimary={true} height={30} />
              </Box>
              <Box className="call-stats">
                <Box className="current-time">{currentTime}</Box>
                {officeContext.state.videocall.screenMode === 'picture-in-picture' && (
                  <Box className="call-timer">{callTimer}</Box>
                )}
              </Box>
            </Box>
            <Box className="center">
              <Fab
                color="primary"
                aria-label={muted ? 'mic-off' : 'mic-on'}
                size="medium"
                className={`other-button ${muted ? 'other-button-disabled' : ''}`}
                disabled={micButtonDisabled}
                onClick={() => muteAudio(!muted)}
              >
                {muted ? <MicOffIcon /> : <MicIcon />}
              </Fab>
              <Fab
                color="primary"
                aria-label={
                  localVideoState === LocalVideoState.Off ? 'videocam-off' : 'videocam-on'
                }
                size="medium"
                className={`other-button ${
                  localVideoState === LocalVideoState.Off ? 'other-button-disabled' : ''
                }`}
                onClick={toggleShowLocalVideo}
              >
                {localVideoState === LocalVideoState.Off ? <VideocamOffIcon /> : <VideocamIcon />}
              </Fab>
              <Fab
                color="primary"
                aria-label="share-screen"
                size="medium"
                className={`other-button ${sharingScreen ? 'other-button-active' : ''}`}
                onClick={() => {
                  // Eval
                  if (sharingScreen) {
                    // Already sharing, wants to stop
                    stopScreenShare();
                  } else {
                    // Not sharing, wants to share
                    startScreenShare();
                  }
                }}
              >
                {sharingScreen ? <CancelPresentationIcon /> : <PresentToAllIcon />}
              </Fab>
              <Fab
                color="primary"
                aria-label="settings"
                size="medium"
                className="other-button"
                onClick={() => openSettings()}
              >
                <SettingsIcon />
              </Fab>
              <Fab
                color="primary"
                aria-label="hang-up"
                className="hang-up-button"
                onClick={() => endCall()}
              >
                <CallEndIcon />
              </Fab>
            </Box>
            <Box className="right">
              {officeContext.state.videocall.screenMode === 'full-screen' && (
                <Box className="call-stats">
                  <Box className="call-timer">{callTimer}</Box>
                </Box>
              )}

              {!isTouch && (
                <IconButton
                  aria-label="picture-in-picture"
                  className="picture-in-picture"
                  size="large"
                  onClick={() =>
                    patchVideocallState({
                      screenMode:
                        videocallStateRef.current.screenMode === 'full-screen'
                          ? 'picture-in-picture'
                          : 'full-screen',
                    })
                  }
                >
                  {officeContext.state.videocall.screenMode === 'full-screen' ? (
                    <PictureInPictureAltIcon />
                  ) : (
                    <AspectRatioIcon />
                  )}
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </Draggable>

      <Draggable
        defaultClassName={`incoming-videocall-draggable ${
          incomingCallVisible ? 'visible' : 'hidden'
        } ${incomingCallPendingRecovery ? 'waiting-recovery' : ''}`}
        bounds="parent"
        disabled={isTouch}
      >
        <Box
          sx={{
            left: areaWidth - spacing - incomingCallPopupWidth,
            top: areaHeight - spacing - incomingCallPopupHeight,
            width: incomingCallPopupWidth,
            height: incomingCallPopupHeight,
          }}
        >
          <Box className="body">
            {incomingCallPendingRecovery && (
              <IconButton
                className="recovery-close-button"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setDismissedRecoveryActionModal(true);
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
            <Box className="picture">
              <Avatar
                className="avatar"
                src={officeContext.state.videocall.callerInfo?.profilePicture}
              >
                <PersonIcon
                  sx={{
                    fontSize: `1.4em`,
                  }}
                />
              </Avatar>
            </Box>
            <Box className="label">
              <Tooltip title={incomingCallCallerName}>
                <Box className="name">{incomingCallCallerName}</Box>
              </Tooltip>
              <Box className="action">{t(translations.videocall.incomingCall)}</Box>
              {incomingCallPendingRecovery && (
                <>
                  <Box className="recovery-action">
                    {t(translations.videocall.incomingCallConnecting)}
                  </Box>
                  <Box className="recovery-action recovery-action-hint">
                    {t(translations.videocall.incomingCallConnectingHint)}
                  </Box>
                </>
              )}
            </Box>
          </Box>
          {!incomingCallPendingRecovery && (
            <Box className="footer">
              <Stack direction="row" spacing={2}>
                <Button color="error" onClick={declineCall}>
                  {t(translations.videocall.decline)}
                </Button>
                <Button color="success" onClick={answerCall}>
                  {t(translations.videocall.answer)}
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Draggable>

      <Dialog
        open={settingsOpen}
        onClose={closeSettings}
        aria-labelledby="settings"
        aria-describedby="settings"
        className="settings"
      >
        <DialogTitle>
          <Box id="video-call-settings-title">
            <Box className="title">{t(translations.videocall.settingsTitle)}</Box>
            <Box className="icon">
              <IconButton aria-label="close" onClick={closeSettings}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            maxWidth: '100%',
            minWidth: '300px',
            marginTop: 0,
            paddingTop: '8px !important',
          }}
        >
          <Box className="video-call-settings-form-select">
            <FormControl fullWidth>
              <InputLabel id="videocall-settings-microphone">
                {t(translations.videocall.settingsMicrophone)}
              </InputLabel>
              <Select
                labelId="videocall-settings-microphone-label"
                id="videocall-settings-microphone-select"
                value={selectedMicrophone}
                label={t(translations.videocall.settingsMicrophone)}
                onChange={(e) => changeMicrophone(e.target.value)}
              >
                {microphones.map((mic) => {
                  return <MenuItem value={mic.id}>{mic.name}</MenuItem>;
                })}
              </Select>
            </FormControl>
          </Box>
          <Box className="video-call-settings-form-select">
            <FormControl fullWidth>
              <InputLabel id="videocall-settings-camera">
                {t(translations.videocall.settingsCamera)}
              </InputLabel>
              <Select
                labelId="videocall-settings-camera-label"
                id="videocall-settings-camera-select"
                value={selectedCamera}
                label={t(translations.videocall.settingsCamera)}
                onChange={(e) => {
                  changeCamera(e.target.value);
                }}
              >
                {cameras.map((camera) => {
                  return <MenuItem value={camera.id}>{camera.name}</MenuItem>;
                })}
              </Select>
            </FormControl>
          </Box>
          <Box className="video-call-settings-form-select">
            <FormControl fullWidth>
              <InputLabel id="videocall-settings-speaker">
                {t(translations.videocall.settingsSpeaker)}
              </InputLabel>
              <Select
                labelId="videocall-settings-speaker-label"
                id="videocall-settings-speaker-select"
                value={selectedSpeaker}
                label={t(translations.videocall.settingsSpeaker)}
                onChange={(e) => changeSpeaker(e.target.value)}
              >
                {speakers.map((speaker) => {
                  return <MenuItem value={speaker.id}>{speaker.name}</MenuItem>;
                })}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={callFailedOpen}
        onClose={closeCallFailed}
        aria-labelledby="call-failed"
        aria-describedby="call-failed"
        className="call-failed"
      >
        <DialogTitle id="call-failed-alert-dialog-title">
          {t(translations.videocall.callFailed.title)}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="call-failed-alert-dialog-description">
            {t(
              (translations.videocall.callFailed as Record<string, string>)[
                `e${callFailedErrorCode}`
              ]
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCallFailed} autoFocus>
            {t(translations.videocall.callFailed.ok)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
